import { InputAdornment, TextField } from "@mui/material"
import type { FC } from "react"
import { useCallback, useEffect, useRef, useState } from "react"

interface CurrencyInputProps {
  value?: string
  onChange: (value: string) => void
  onBlur?: () => void
  error?: boolean
  helperText?: string
  placeholder?: string
  fullWidth?: boolean
  label?: string
  disabled?: boolean
  className?: string
}

// Helper function to clean and format input
export const cleanCurrencyInput = (
  input: string,
  allowTrailingDot = true
): { cleaned: string; cursorOffset: number } => {
  // Remove all non-numeric characters except decimal point
  let cleaned = input.replace(/[^\d.]/g, "")

  // Handle multiple decimal points - keep only the first one
  const decimalIndex = cleaned.indexOf(".")
  if (decimalIndex !== -1) {
    const beforeDecimal = cleaned.substring(0, decimalIndex)
    const afterDecimal = cleaned.substring(decimalIndex + 1).replace(/\./g, "")
    // Limit to 2 decimal places
    const limitedAfterDecimal = afterDecimal.substring(0, 2)

    // Include decimal point if there are decimal digits OR if we're allowing trailing dots
    if (limitedAfterDecimal || allowTrailingDot) {
      cleaned = beforeDecimal + "." + limitedAfterDecimal
    } else {
      cleaned = beforeDecimal
    }
  }

  // Remove leading zeros but keep single zero before decimal
  if (cleaned.length > 1 && cleaned[0] === "0" && cleaned[1] !== ".") {
    cleaned = cleaned.substring(1)
  }

  return { cleaned, cursorOffset: 0 }
}

// Helper function to format for display with currency symbols
const formatForDisplay = (value: string): string => {
  if (!value) return ""

  const { cleaned } = cleanCurrencyInput(value)
  if (!cleaned) return ""

  const result = new Intl.NumberFormat("en", {
    maximumFractionDigits: 2,
  }).format(parseFloat(cleaned))

  // workaround to allow trailing dot
  return cleaned[cleaned.length - 1] === "." ? `$${result}.` : "$" + result
}

// Helper to get numeric value for form submission
const getNumericValue = (displayValue: string): string => {
  const { cleaned } = cleanCurrencyInput(displayValue, false) // Don't allow trailing dot for form values
  return cleaned
}

export const CurrencyInput: FC<CurrencyInputProps> = ({
  value = "",
  onChange,
  onBlur,
  error,
  helperText,
  placeholder = "$0.00",
  fullWidth = true,
  label,
  disabled,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [displayValue, setDisplayValue] = useState(() => {
    return value ? formatForDisplay(value) : ""
  })

  // Track cursor position for maintaining it during formatting
  const [cursorPosition, setCursorPosition] = useState<number | null>(null)

  // Sync internal state with external value prop
  useEffect(() => {
    if (value !== undefined) {
      const formatted = value ? formatForDisplay(value) : ""
      setDisplayValue(formatted)
    }
  }, [value])

  // Restore cursor position after formatting
  useEffect(() => {
    if (cursorPosition !== null && inputRef.current) {
      const input = inputRef.current.querySelector("input")
      if (input && typeof input.setSelectionRange === "function") {
        input.setSelectionRange(cursorPosition, cursorPosition)
      }
      setCursorPosition(null)
    }
  }, [cursorPosition, displayValue])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value
      const currentCursorPos = event.target.selectionStart || 0

      // Allow empty input
      if (inputValue === "") {
        setDisplayValue("")
        onChange("")
        return
      }

      // Clean and format the input
      const { cleaned } = cleanCurrencyInput(inputValue)

      if (cleaned) {
        const formatted = formatForDisplay(cleaned)
        const numericValue = getNumericValue(formatted)

        // Determine if we're adding or removing characters
        const oldValue = displayValue
        const newValue = formatted
        const wasTyping = inputValue.length > oldValue.length

        let newCursorPos = currentCursorPos

        if (wasTyping) {
          // When typing new characters, move cursor forward accounting for new formatting
          const lengthDiff = newValue.length - oldValue.length
          newCursorPos = currentCursorPos + lengthDiff
        } else {
          // When deleting, count digits to maintain logical position
          const beforeCursorOld = oldValue.substring(0, currentCursorPos)
          const digitCountBefore = (beforeCursorOld.match(/\d/g) || []).length

          // Find position in new value that has same number of digits
          newCursorPos = 1 // Start after $
          let digitsSeen = 0

          for (let i = 1; i < newValue.length; i++) {
            if (/\d/.test(newValue[i])) {
              digitsSeen++
            }
            if (digitsSeen >= digitCountBefore) {
              newCursorPos = i + 1
              break
            }
          }
        }

        // Ensure cursor doesn't go beyond the string or before $
        newCursorPos = Math.max(1, Math.min(newCursorPos, newValue.length))

        setDisplayValue(formatted)
        onChange(numericValue)
        setCursorPosition(newCursorPos)
      } else {
        setDisplayValue("")
        onChange("")
      }
    },
    [displayValue, onChange]
  )

  return (
    <TextField
      ref={inputRef}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      fullWidth={fullWidth}
      label={label}
      disabled={disabled}
      className={className}
      slotProps={{
        htmlInput: {
          inputMode: "decimal",
          // eslint-disable-next-line no-useless-escape
          pattern: "[0-9]*\.?[0-9]*",
        },
        input: {
          startAdornment: !displayValue ? <InputAdornment position='start'>$</InputAdornment> : undefined,
        },
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused input": {
            border: "none",
            outline: "none",
            boxShadow: "none",
          },
        },
      }}
    />
  )
}
